import { CapacitorHttp } from '@capacitor/core';
import { Capacitor } from '@capacitor/core';

// YouTube Player API 키 (공개 키)
const YOUTUBE_API_KEY = 'AIzaSyAO_FJ2SlqU8Q4STEHLGCilw_Y9_11qcW8';

/**
 * 모바일 네이티브 전용 YouTube 자막 추출 서비스
 * CapacitorHttp를 사용하여 CORS 제한 없이 YouTube API 직접 호출
 */
export class NativeTranscriptService {
  constructor() {
    this.isNative = Capacitor.isNativePlatform();
    this.platform = Capacitor.getPlatform();
  }

  /**
   * YouTube URL에서 비디오 ID 추출
   */
  extractVideoId(url) {
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
      /^([a-zA-Z0-9_-]{11})$/ // 직접 비디오 ID
    ];

    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) {
        return match[1];
      }
    }

    throw new Error(`Invalid YouTube URL: ${url}`);
  }

  /**
   * YouTube Player API 호출
   */
  async fetchPlayerApi(videoId) {
    if (!this.isNative) {
      throw new Error('Native transcript service can only be used on mobile platforms');
    }

    console.log('🚀 NATIVE 클라이언트 직접 추출 시작', { 
      videoId, 
      platform: this.platform 
    });

    const url = `https://www.youtube.com/youtubei/v1/player?key=${YOUTUBE_API_KEY}`;
    
    console.log('🎯 YouTube API 직접 호출 (서버 우회)', {
      targetUrl: url,
      method: 'POST',
      service: 'CapacitorHttp',
      note: 'TubeDigest 백엔드 서버를 전혀 거치지 않음'
    });
    
    const payload = {
      context: {
        client: {
          clientName: 'WEB',
          clientVersion: '2.20210721.00.00'
        }
      },
      videoId: videoId
    };

    try {
      const options = {
        url: url,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'User-Agent': 'Mozilla/5.0 (iPhone; CPU iPhone OS 15_0 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/15.0 Mobile/15.0 Safari/604.1'
        },
        data: payload
      };

      console.log('📡 CapacitorHttp 네이티브 요청 전송', { 
        url, 
        platform: this.platform,
        corsStatus: 'BYPASSED - 네이티브 HTTP 클라이언트 사용'
      });

      const response = await CapacitorHttp.request(options);
      
      console.log('✅ YouTube API 응답 수신 성공', { 
        status: response.status,
        dataLength: response.data ? JSON.stringify(response.data).length : 0,
        platform: this.platform,
        source: 'DIRECT_YOUTUBE_API',
        serverUsed: 'NONE - 완전한 클라이언트 사이드 추출'
      });

      if (response.status !== 200) {
        throw new Error(`HTTP ${response.status}: ${response.data}`);
      }

      return response.data;
    } catch (error) {
      console.error('❌ YouTube API 직접 호출 실패', { 
        error: error.message,
        videoId,
        platform: this.platform,
        note: '이 오류는 YouTube API에서 직접 발생한 것입니다'
      });
      throw error;
    }
  }

  /**
   * Player API 응답에서 자막 트랙 파싱 (백엔드와 동일한 로직)
   */
  parseCaptionTracks(playerResponse) {
    try {
      console.log('🔍 [백엔드 호환] 자막 메타데이터 추출 시작');
      
      const captionData = [];
      
      // 전체 응답을 문자열로 변환하여 패턴 매칭
      const responseStr = JSON.stringify(playerResponse);
      console.log('📊 [백엔드 호환] 응답 크기:', responseStr.length);
      
      // 방법 1: captionTracks JSON 구조 파싱 (백엔드와 동일한 다중 패턴)
      const captionTracksPatterns = [
        /"captionTracks":\s*\[(.*?)\]/s,  // 기본 패턴
        /"captionTracks":\[(.*?)\]/s,     // 공백 없는 패턴
        /captionTracks":\s*\[(.*?)\]/s,   // 앞쪽 따옴표 없는 패턴
      ];
      
      let captionTracksMatch = null;
      for (const pattern of captionTracksPatterns) {
        captionTracksMatch = responseStr.match(pattern);
        if (captionTracksMatch) {
          console.log('✅ [백엔드 호환] captionTracks 패턴 매칭 성공');
          break;
        }
      }
      
      if (captionTracksMatch) {
        const tracksContent = captionTracksMatch[1];
        console.log('📄 [백엔드 호환] captionTracks 내용 길이:', tracksContent.length);
        
        // 백엔드와 동일한 중첩 JSON 구조 파싱 (bracket counting)
        let bracketCount = 0;
        let startPos = 0;
        const trackObjects = [];
        
        for (let i = 0; i < tracksContent.length; i++) {
          const char = tracksContent[i];
          if (char === '{') {
            if (bracketCount === 0) {
              startPos = i;
            }
            bracketCount++;
          } else if (char === '}') {
            bracketCount--;
            if (bracketCount === 0) {
              const trackObj = tracksContent.substring(startPos, i + 1);
              trackObjects.push(trackObj);
            }
          }
        }
        
        console.log('🎯 [백엔드 호환] 파싱된 track 객체 수:', trackObjects.length);
        
        for (const trackObj of trackObjects) {
          if (trackObj.includes('baseUrl') && trackObj.includes('languageCode')) {
            // baseUrl 추출
            const baseUrlMatch = trackObj.match(/"baseUrl":\s*"([^"]*)"/);
            const langMatch = trackObj.match(/"languageCode":\s*"([^"]*)"/);
            const vssMatch = trackObj.match(/"vssId":\s*"([^"]*)"/);
            const kindMatch = trackObj.match(/"kind":\s*"([^"]*)"/);
            
            if (baseUrlMatch && langMatch) {
              const baseUrl = baseUrlMatch[1].replace(/\\u0026/g, '&');
              const langCode = langMatch[1];
              const vssId = vssMatch ? vssMatch[1] : `.${langCode}`;
              const kind = kindMatch ? kindMatch[1] : 'standard';
              
              // 백엔드와 동일한 자동/수동 구분
              const isAuto = kind === 'asr' || vssId.startsWith('a.');
              
              // 중복 제거 (같은 언어와 타입)
              const existing = captionData.find(c => 
                c.languageCode === langCode && c.isAuto === isAuto
              );
              
              if (!existing) {
                captionData.push({
                  baseUrl: baseUrl,
                  languageCode: langCode,
                  vssId: vssId,
                  kind: kind,
                  isAuto: isAuto,
                  name: { simpleText: `${langCode} (${isAuto ? '자동' : '수동'})` }
                });
                
                console.log(`🎯 [백엔드 호환] 자막 발견: ${langCode} (${isAuto ? '자동' : '수동'}) - ${vssId}`);
              }
            }
          }
        }
      }
      
      // 방법 2: URL 패턴 추출 (백엔드 fallback과 동일)
      if (captionData.length === 0) {
        console.log('⚠️ [백엔드 호환] JSON 메타데이터 파싱 실패, URL 패턴으로 fallback');
        
        // 디버깅 정보
        if (responseStr.includes('captionTracks')) {
          console.log('🔍 [백엔드 호환] 응답에 captionTracks 있음, 하지만 파싱 실패');
          const captionStart = responseStr.indexOf('"captionTracks"');
          if (captionStart !== -1) {
            const sample = responseStr.substring(captionStart, captionStart + 200);
            console.log('📋 [백엔드 호환] captionTracks 샘플:', sample);
          }
        } else {
          console.log('❌ [백엔드 호환] 응답에 captionTracks 없음');
        }
        
        // timedtext URL 패턴 추출
        const timedtextPattern = /https:\/\/www\.youtube\.com\/api\/timedtext[^"']*/g;
        const urls = [...new Set(responseStr.match(timedtextPattern) || [])];
        
        console.log('🔍 [백엔드 호환] timedtext URL 발견:', urls.length + '개');
        
        for (const url of urls) {
          // URL에서 언어 코드 추출
          const langMatch = url.match(/[?&]lang=([^&]*)/);
          if (langMatch) {
            const langCode = langMatch[1];
            
            // 백엔드와 동일한 자동/수동 구분 로직
            let isAuto = false;
            let vssId = `.${langCode}`;
            let kind = 'standard';
            
            // 자동 생성 자막 URL 패턴들 (백엔드와 동일)
            const autoIndicators = [
              '&kind=asr',      // ASR 종류
              '&asr_langs=',    // ASR 언어 설정
              '&caps=asr',      // ASR 캡션
              '&tlang=',        // 자동 번역
              'fmt=srv3',       // 서버 형식 (자동)
              'xorb=2',         // 특정 자동 파라미터
              'xoaf=5',         // 특정 자동 파라미터
            ];
            
            for (const indicator of autoIndicators) {
              if (url.includes(indicator)) {
                isAuto = true;
                vssId = `a.${langCode}`;
                kind = 'asr';
                break;
              }
            }
            
            captionData.push({
              baseUrl: url,
              languageCode: langCode,
              vssId: vssId,
              kind: kind,
              isAuto: isAuto,
              name: { simpleText: `${langCode} (${isAuto ? '자동' : '수동'})` }
            });
            
            console.log(`🎯 [백엔드 호환] 자막 발견: ${langCode} (${isAuto ? '자동' : '수동'}) - URL 패턴`);
          }
        }
      }
      
      if (captionData.length === 0) {
        throw new Error('No caption tracks found');
      }

      console.log('✅ [백엔드 호환] 자막 트랙 파싱 완료:', { 
        count: captionData.length,
        languages: captionData.map(track => `${track.languageCode}(${track.isAuto ? '자동' : '수동'})`)
      });

      return captionData;
    } catch (error) {
      console.error('❌ [백엔드 호환] 자막 트랙 파싱 실패:', { error: error.message });
      throw new Error('Failed to parse caption tracks: ' + error.message);
    }
  }

  /**
   * 자동 생성 자막으로부터 원본 언어 감지 (백엔드 호환)
   */
  detectOriginalLanguageFromAutoCaptions(captionTracks) {
    const autoCaptions = captionTracks.filter(track => track.isAuto);
    
    if (autoCaptions.length === 0) {
      console.log('🔍 [백엔드 호환] 자동 생성 자막 없음 - 원본 언어 감지 불가');
      return null;
    }
    
    // 백엔드와 동일한 원본 언어 우선순위
    const originalLanguagePriorities = [
      'en',      // 영어 (YouTube 대부분 영상)
      'en-US',   // 미국 영어
      'ko',      // 한국어
      'ja',      // 일본어  
      'zh',      // 중국어
      'zh-CN',   // 중국어 간체
      'zh-TW',   // 중국어 번체
      'es',      // 스페인어
      'fr',      // 프랑스어
      'de',      // 독일어
    ];
    
    // 우선순위에 따라 자동 생성 자막 찾기
    for (const priorityLang of originalLanguagePriorities) {
      for (const autoCap of autoCaptions) {
        if (autoCap.languageCode.startsWith(priorityLang)) {
          console.log(`🔍 [백엔드 호환] 우선순위 기반 원본 언어 감지: ${autoCap.languageCode}`);
          return autoCap.languageCode;
        }
      }
    }
    
    // 우선순위에 없으면 첫 번째 자동 생성 자막 사용
    const originalLang = autoCaptions[0].languageCode;
    console.log(`🔍 [백엔드 호환] 기본 자동 생성 자막으로 원본 언어 감지: ${originalLang}`);
    return originalLang;
  }

  /**
   * 특정 언어의 최적 자막 찾기 (백엔드 호환)
   */
  findBestCaptionByLanguage(captionTracks, language, preferManual = true) {
    const langCaptions = captionTracks.filter(track => track.languageCode === language);
    
    if (langCaptions.length === 0) {
      return null;
    }
    
    if (preferManual) {
      // 수동 자막 우선
      const manualCaptions = langCaptions.filter(track => !track.isAuto);
      if (manualCaptions.length > 0) {
        return manualCaptions[0]; // 첫 번째 수동 자막
      }
    }
    
    // 자동 자막 또는 수동이 없는 경우
    const autoCaptions = langCaptions.filter(track => track.isAuto);
    if (autoCaptions.length > 0) {
      return autoCaptions[0]; // 첫 번째 자동 자막
    }
    
    // 어떤 자막이든 있으면 반환
    return langCaptions.length > 0 ? langCaptions[0] : null;
  }

  /**
   * 품질 점수로 최적 수동 자막 선택 (백엔드 호환)
   */
  findBestManualCaptionByQuality(captionTracks) {
    const manualCaptions = captionTracks.filter(track => !track.isAuto);
    
    if (manualCaptions.length === 0) {
      return null;
    }
    
    // 백엔드와 동일한 언어별 우선순위 점수
    const languageScores = {
      'ko': 10,  // 한국어
      'en': 9,   // 영어  
      'ja': 8,   // 일본어
      'zh': 7,   // 중국어
      'es': 6,   // 스페인어
      'fr': 5,   // 프랑스어
      'de': 4,   // 독일어
    };
    
    let bestCaption = null;
    let bestScore = -1;
    
    for (const caption of manualCaptions) {
      const lang = caption.languageCode;
      const score = languageScores[lang] || 1; // 기본 점수 1
      
      if (score > bestScore) {
        bestScore = score;
        bestCaption = caption;
      }
    }
    
    console.log(`🔍 [백엔드 호환] 최적 수동 자막 선택: ${bestCaption.languageCode} (점수: ${bestScore})`);
    return bestCaption;
  }

  /**
   * 자막 콘텐츠 다운로드
   */
  async fetchCaptionContent(captionUrl) {
    try {
      // XML 형식으로 요청하기 위해 fmt 파라미터 추가
      const url = captionUrl.includes('fmt=') 
        ? captionUrl 
        : captionUrl + '&fmt=ttml';

      console.debug('Fetching caption content', { url });

      const options = {
        url: url,
        method: 'GET',
        headers: {
          'User-Agent': 'Mozilla/5.0 (iPhone; CPU iPhone OS 15_0 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/15.0 Mobile/15E148 Safari/604.1'
        }
      };

      const response = await CapacitorHttp.request(options);

      if (response.status !== 200) {
        throw new Error(`HTTP ${response.status}: ${response.data}`);
      }

      // XML 내용을 로그로 출력하여 형식 확인
      console.log('📄 실제 자막 XML 내용 (처음 500자)', { 
        xmlPreview: response.data ? response.data.toString().substring(0, 500) : 'NO_DATA',
        xmlLength: response.data ? response.data.toString().length : 0
      });

      return response.data;
    } catch (error) {
      console.error('Failed to fetch caption content', { error: error.message });
      throw error;
    }
  }



  /**
   * 시간 문자열을 초로 변환 (TTML 형식: HH:MM:SS.mmm)
   */
  timeStringToSeconds(timeStr) {
    const match = timeStr.match(/(\d+):(\d+):(\d+)\.?(\d+)?/);
    if (!match) return 0;
    
    const hours = parseInt(match[1]) || 0;
    const minutes = parseInt(match[2]) || 0;
    const seconds = parseInt(match[3]) || 0;
    const milliseconds = parseInt(match[4] || 0);
    
    return hours * 3600 + minutes * 60 + seconds + milliseconds / 1000;
  }

  /**
   * 백엔드와 동일한 XML 파싱 로직 (YouTube <text> 형식)
   */
  formatTranscriptData(xmlContent) {
    try {
      console.log('🔍 백엔드 호환 XML 파싱 시작', { 
        type: 'YouTube_XML', 
        length: xmlContent.length,
        preview: xmlContent.substring(0, 200) 
      });

      const transcriptArray = [];

      // YouTube는 실제로 TTML 형식을 사용: <p begin="..." end="...">텍스트</p>
      const realTtmlPattern = /<p\s+begin="([^"]+)"\s+end="([^"]+)"[^>]*>(.*?)<\/p>/gs;
      
      console.log('🔍 TTML <p> 패턴 테스트 중...');
      
      let match;
      let matchCount = 0;
      
      // 먼저 실제 TTML 형식 시도
      while ((match = realTtmlPattern.exec(xmlContent)) !== null) {
        matchCount++;
        const [, startTime, endTime, text] = match;
        
        // 시간 형식 변환 (HH:MM:SS.mmm -> seconds)
        const startSeconds = this.timeStringToSeconds(startTime);
        const endSeconds = this.timeStringToSeconds(endTime);
        
        // 백엔드와 동일한 HTML 엔티티 디코딩
        let cleanText = text || "";
        cleanText = cleanText.replace(/&amp;/g, '&');
        cleanText = cleanText.replace(/&lt;/g, '<');
        cleanText = cleanText.replace(/&gt;/g, '>');
        cleanText = cleanText.replace(/&quot;/g, '"');
        cleanText = cleanText.replace(/&#39;/g, "'");
        cleanText = cleanText.trim();

        // 빈 텍스트나 음악 표시 제외 (백엔드와 동일)
        if (cleanText && !cleanText.startsWith('[') && cleanText !== '[Music]') {
          transcriptArray.push({
            text: cleanText,
            start: startSeconds,
            duration: Math.max(0.1, endSeconds - startSeconds) // 최소 0.1초
          });
        }
      }

      if (transcriptArray.length > 0) {
        console.log('✅ TTML <p> 파싱 성공', { 
          pattern: 'TTML <p begin="" end="">',
          matchCount,
          sampleMatches: transcriptArray.slice(0, 3)
        });

        return {
          transcript: transcriptArray,
          extractionInfo: {
            method: 'DIRECT_YOUTUBE_API',
            corsStatus: 'BYPASSED_WITH_CAPACITOR_HTTP',
            serverUsed: 'NONE',
            clientSideExtraction: true,
            source: 'native',
            format: 'TTML_P_TAG',
            totalCues: transcriptArray.length
          }
        };
      }

      // 백엔드 호환 패턴도 시도: <text start="..." dur="...">텍스트</text>  
      const youtubePattern = /<text\s+start="([^"]+)"\s+dur="([^"]+)"[^>]*>(.*?)<\/text>/gs;
      
      console.log('🔍 YouTube <text> 패턴 테스트 중...');
      matchCount = 0;
      
      while ((match = youtubePattern.exec(xmlContent)) !== null) {
        matchCount++;
        const [, startTime, duration, text] = match;
        
        // 백엔드와 동일한 HTML 엔티티 디코딩
        let cleanText = text || "";
        cleanText = cleanText.replace(/&amp;/g, '&');
        cleanText = cleanText.replace(/&lt;/g, '<');
        cleanText = cleanText.replace(/&gt;/g, '>');
        cleanText = cleanText.replace(/&quot;/g, '"');
        cleanText = cleanText.replace(/&#39;/g, "'");
        cleanText = cleanText.trim();

        // 빈 텍스트나 음악 표시 제외 (백엔드와 동일)
        if (cleanText && !cleanText.startsWith('[') && cleanText !== '[Music]') {
          transcriptArray.push({
            text: cleanText,
            start: parseFloat(startTime),
            duration: parseFloat(duration)
          });
        }
      }

      console.log('✅ YouTube XML 파싱 완료', { 
        pattern: 'YouTube <text start="" dur="">',
        matchCount,
        sampleMatches: transcriptArray.slice(0, 3)
      });

      if (transcriptArray.length > 0) {
        return {
          transcript: transcriptArray,
          extractionInfo: {
            method: 'DIRECT_YOUTUBE_API',
            corsStatus: 'BYPASSED_WITH_CAPACITOR_HTTP',
            serverUsed: 'NONE',
            clientSideExtraction: true,
            source: 'native',
            format: 'YouTube_XML',
            totalCues: transcriptArray.length
          }
        };
      }

      // 대체 패턴들 (기존 로직 유지)
      console.log('🔍 대체 패턴 시도 중...');
      
      // 패턴 2: TTML 스타일 (혹시 다른 형식이 올 경우)
      const ttmlPattern = /<p\s+begin="([^"]+)"\s+end="([^"]+)"[^>]*>(.*?)<\/p>/gs;
      matchCount = 0;
      
      while ((match = ttmlPattern.exec(xmlContent)) !== null) {
        matchCount++;
        const [, startTime, endTime, text] = match;
        const cleanText = text.replace(/<[^>]*>/g, '').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&#39;/g, "'").trim();
        
        if (cleanText && !cleanText.startsWith('[') && cleanText !== '[Music]') {
          // 시간 형식 변환 (HH:MM:SS.mmm -> seconds)
          const timeMatch = startTime.match(/(\d+):(\d+):(\d+)\.?(\d+)?/);
          if (timeMatch) {
            const hours = parseInt(timeMatch[1]);
            const minutes = parseInt(timeMatch[2]);
            const seconds = parseInt(timeMatch[3]);
            const milliseconds = parseInt(timeMatch[4] || 0);
            const totalSeconds = hours * 3600 + minutes * 60 + seconds + milliseconds / 1000;
            
            transcriptArray.push({
              text: cleanText,
              start: totalSeconds,
              duration: 2 // 기본 duration
            });
          }
        }
      }

      if (transcriptArray.length > 0) {
        console.log('✅ TTML 패턴 성공', { matchCount });
        return {
          transcript: transcriptArray,
          extractionInfo: {
            method: 'DIRECT_YOUTUBE_API',
            corsStatus: 'BYPASSED_WITH_CAPACITOR_HTTP',
            serverUsed: 'NONE',
            clientSideExtraction: true,
            source: 'native',
            format: 'TTML',
            totalCues: transcriptArray.length
          }
        };
      }

      console.error('❌ 모든 패턴 실패', { 
        xmlContentPreview: xmlContent.substring(0, 1000),
        patternsAttempted: ['YouTube_XML', 'TTML']
      });

      throw new Error('No transcript data to format');

    } catch (error) {
      console.error('[ERROR] XML 파싱 실패', { 
        error: error.message,
        xmlPreview: xmlContent ? xmlContent.substring(0, 500) : 'null'
      });
      throw error;
    }
  }

  /**
   * 백엔드와 동일한 자막 텍스트 포맷팅
   */
  formatTranscript(transcript, includeTimestamp = true) {
    if (!Array.isArray(transcript) || transcript.length === 0) {
      throw new Error('No transcript data to format');
    }

    if (!includeTimestamp) {
      return transcript.map(entry => entry.text).join('\n');
    }

    // 백엔드와 동일한 포맷팅 로직
    let formattedTranscript = "";
    
    for (const entry of transcript) {
      const startTime = entry.start;
      const text = entry.text;
      
      // 시간을 [mm:ss] 형식으로 변환 (백엔드와 동일)
      const minutes = Math.floor(startTime / 60);
      const seconds = Math.floor(startTime % 60);
      const timeStr = `[${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}]`;
      
      formattedTranscript += `${timeStr} ${text}\n`;
    }
    
    return formattedTranscript.trim();
  }

  /**
   * 메인 자막 추출 함수
   */
  async extractTranscript(videoUrl, languageCode = null, includeTimestamp = true) {
    if (!this.isNative) {
      throw new Error('This service can only be used on mobile platforms');
    }

    const startTime = Date.now();
    
    try {
      console.log('Starting native transcript extraction', { 
        videoUrl, 
        languageCode, 
        platform: this.platform 
      });

      // 1. 비디오 ID 추출
      const videoId = this.extractVideoId(videoUrl);

      // 2. Player API 호출
      const playerResponse = await this.fetchPlayerApi(videoId);

      // 3. 자막 트랙 파싱
      const captionTracks = this.parseCaptionTracks(playerResponse);

      // 4. 백엔드와 동일한 자막 선택 로직 (완전 교체)
      console.log('🔍 [백엔드 호환] 자막 선택 프로세스 시작');
      
      // 1단계: 자동 생성 자막으로 원본 언어 감지
      const originalLanguage = this.detectOriginalLanguageFromAutoCaptions(captionTracks);
      console.log(`🔍 [백엔드 호환] 감지된 원본 언어: ${originalLanguage}`);
      
      let selectedTrack = null;
      
      // 2단계: 원본 언어의 수동 자막 우선 선택
      if (originalLanguage) {
        console.log(`🔍 [백엔드 호환] 원본 언어 ${originalLanguage}의 수동 자막 찾기`);
        const manualCaption = this.findBestCaptionByLanguage(captionTracks, originalLanguage, true);
        if (manualCaption) {
          selectedTrack = manualCaption;
          console.log(`✅ [백엔드 호환] 원본 언어 ${originalLanguage}의 수동 자막 선택됨`);
        } else {
          // 3단계: 원본 언어의 자동 자막 (수동이 없는 경우)
          console.log(`🔍 [백엔드 호환] 원본 언어 ${originalLanguage}의 자동 자막 찾기`);
          const autoCaption = this.findBestCaptionByLanguage(captionTracks, originalLanguage, false);
          if (autoCaption) {
            selectedTrack = autoCaption;
            console.log(`✅ [백엔드 호환] 원본 언어 ${originalLanguage}의 자동 자막 선택됨`);
          }
        }
      }
      
      // 4단계: 자동 생성 자막이 없으면 수동 자막 중 품질 점수로 선택
      if (!selectedTrack) {
        console.log(`🔍 [백엔드 호환] 자동 생성 자막 없음, 수동 자막 중 최적 선택`);
        const bestManualCaption = this.findBestManualCaptionByQuality(captionTracks);
        if (bestManualCaption) {
          selectedTrack = bestManualCaption;
          console.log(`✅ [백엔드 호환] 최적 수동 자막 선택됨: ${bestManualCaption.languageCode}`);
        }
      }
      
      // 5단계: 여전히 없으면 첫 번째 자막 사용
      if (!selectedTrack && captionTracks.length > 0) {
        selectedTrack = captionTracks[0];
        console.log(`⚠️ [백엔드 호환] 첫 번째 자막 사용: ${selectedTrack.languageCode}`);
      }
      
      if (!selectedTrack) {
        throw new Error('No caption tracks available');
      }
      
      console.log(`🎯 [백엔드 호환] 최종 선택된 자막: ${selectedTrack.languageCode} (${selectedTrack.isAuto ? '자동' : '수동'})`);

      // 5. 자막 콘텐츠 다운로드 및 파싱 (백엔드 호환)
      const captionContent = await this.fetchCaptionContent(selectedTrack.baseUrl);

      // 6. 새로운 TTML 파싱 로직 사용
      const extractionResult = this.formatTranscriptData(captionContent);

      // 7. 포맷팅
      const formattedTranscript = this.formatTranscript(extractionResult.transcript, includeTimestamp);

      const duration = Date.now() - startTime;
      
      // 자막 내용을 로그로 출력 (처음 500자)
      const transcriptPreview = formattedTranscript.length > 500 
        ? formattedTranscript.substring(0, 500) + '...' 
        : formattedTranscript;
        
      console.log('✅ 네이티브 자막 추출 완료', { 
        videoId, 
        language: languageCode || 'auto',
        transcriptLength: formattedTranscript.length,
        platform: this.platform,
        source: 'CLIENT_SIDE_NATIVE'
      });

      // 실제 자막 내용 출력
      console.log('📄 추출된 자막 내용 (처음 500자):', transcriptPreview);
      console.log('📊 자막 통계', {
        총길이: formattedTranscript.length,
        줄수: formattedTranscript.split('\n').length,
        첫줄: formattedTranscript.split('\n')[0] || '없음',
        마지막줄: formattedTranscript.split('\n').slice(-1)[0] || '없음'
      });

      return {
        success: true,
        transcript: formattedTranscript,
        source: 'native',
        platform: this.platform,
        language: selectedTrack.languageCode,  // 실제 선택된 자막의 언어 코드 사용
        sourceLanguage: selectedTrack.languageCode,  // sourceLanguage 필드 추가
        extractionInfo: {
          method: 'DIRECT_YOUTUBE_API',
          corsStatus: 'BYPASSED_WITH_CAPACITOR_HTTP',
          serverUsed: 'NONE',
          clientSideExtraction: true,
          timestamp: new Date().toISOString()
        }
      };

    } catch (error) {
      const duration = Date.now() - startTime;
      console.error('Native transcript extraction failed', { 
        error: error.message,
        duration: `${duration}ms`,
        platform: this.platform
      });
      throw error;
    }
  }
}

// 싱글톤 인스턴스 생성
export const nativeTranscriptService = new NativeTranscriptService();

// 편의 함수
export async function extractNativeTranscript(videoUrl, languageCode = null, includeTimestamp = true, preValidatedVideoId = null) {
  // 🔧 이미 검증된 videoId가 있으면 사용, 없으면 기존 방식으로 추출
  if (preValidatedVideoId) {
    console.log('🔄 [네이티브 자막] 이미 검증된 videoId 사용:', { videoId: preValidatedVideoId, skipValidation: true });
    
    // NativeTranscriptService 인스턴스 생성
    const service = new NativeTranscriptService();
    
    // 직접 fetchPlayerApi부터 호출하여 URL 검증 단계 건너뛰기
    try {
      const startTime = Date.now();
      
      console.log('Starting native transcript extraction with pre-validated videoId', { 
        videoUrl, 
        videoId: preValidatedVideoId,
        languageCode, 
        platform: service.platform 
      });

      // 2. Player API 호출 (URL 검증 건너뛰기)
      const playerResponse = await service.fetchPlayerApi(preValidatedVideoId);

      // 3. 자막 트랙 파싱
      const captionTracks = service.parseCaptionTracks(playerResponse);

      // 4. 백엔드와 동일한 자막 선택 로직 (완전 교체)
      console.log('🔍 [백엔드 호환] 자막 선택 프로세스 시작');
      
      // 1단계: 자동 생성 자막으로 원본 언어 감지
      const originalLanguage = service.detectOriginalLanguageFromAutoCaptions(captionTracks);
      console.log(`🔍 [백엔드 호환] 감지된 원본 언어: ${originalLanguage}`);
      
      let selectedTrack = null;
      
      // 2단계: 원본 언어의 수동 자막 우선 선택
      if (originalLanguage) {
        console.log(`🔍 [백엔드 호환] 원본 언어 ${originalLanguage}의 수동 자막 찾기`);
        const manualCaption = service.findBestCaptionByLanguage(captionTracks, originalLanguage, true);
        if (manualCaption) {
          selectedTrack = manualCaption;
          console.log(`✅ [백엔드 호환] 원본 언어 ${originalLanguage}의 수동 자막 선택됨`);
        } else {
          // 3단계: 원본 언어의 자동 자막 (수동이 없는 경우)
          console.log(`🔍 [백엔드 호환] 원본 언어 ${originalLanguage}의 자동 자막 찾기`);
          const autoCaption = service.findBestCaptionByLanguage(captionTracks, originalLanguage, false);
          if (autoCaption) {
            selectedTrack = autoCaption;
            console.log(`✅ [백엔드 호환] 원본 언어 ${originalLanguage}의 자동 자막 선택됨`);
          }
        }
      }
      
      // 4단계: 자동 생성 자막이 없으면 수동 자막 중 품질 점수로 선택
      if (!selectedTrack) {
        console.log(`🔍 [백엔드 호환] 자동 생성 자막 없음, 수동 자막 중 최적 선택`);
        const bestManualCaption = service.findBestManualCaptionByQuality(captionTracks);
        if (bestManualCaption) {
          selectedTrack = bestManualCaption;
          console.log(`✅ [백엔드 호환] 최적 수동 자막 선택됨: ${bestManualCaption.languageCode}`);
        }
      }
      
             // 5단계: 여전히 없으면 첫 번째 자막 사용
       if (!selectedTrack && captionTracks.length > 0) {
         selectedTrack = captionTracks[0];
         console.log(`⚠️ [백엔드 호환] 첫 번째 자막 사용: ${selectedTrack.languageCode}`);
       }
       
       if (!selectedTrack) {
         throw new Error('No caption tracks available');
       }
       
                       console.log(`🎯 [백엔드 호환] 최종 선택된 자막: ${selectedTrack.languageCode} (${selectedTrack.isAuto ? '자동' : '수동'})`);
 
        // 5. 자막 콘텐츠 다운로드 및 파싱 (백엔드 호환)
      const captionContent = await service.fetchCaptionContent(selectedTrack.baseUrl);

      // 6. 새로운 TTML 파싱 로직 사용
      const extractionResult = service.formatTranscriptData(captionContent);

      // 7. 포맷팅
      const formattedTranscript = service.formatTranscript(extractionResult.transcript, includeTimestamp);

      const duration = Date.now() - startTime;
      
      // 자막 내용을 로그로 출력 (처음 500자)
      const transcriptPreview = formattedTranscript.length > 500 
        ? formattedTranscript.substring(0, 500) + '...' 
        : formattedTranscript;
        
      console.log('✅ 네이티브 자막 추출 완료 (사전 검증된 videoId 사용)', { 
        videoId: preValidatedVideoId, 
        language: languageCode || 'auto',
        transcriptLength: formattedTranscript.length,
        platform: service.platform,
        source: 'CLIENT_SIDE_NATIVE',
        duration: `${duration}ms`
      });

      // 실제 자막 내용 출력
      console.log('📄 추출된 자막 내용 (처음 500자):', transcriptPreview);

      return {
        success: true,
        transcript: formattedTranscript,
        source: 'native',
        platform: service.platform,
        language: selectedTrack.languageCode,  // 실제 선택된 자막의 언어 코드 사용
        sourceLanguage: selectedTrack.languageCode,  // sourceLanguage 필드 추가
        extractionInfo: {
          method: 'DIRECT_YOUTUBE_API_PRE_VALIDATED',
          corsStatus: 'BYPASSED_WITH_CAPACITOR_HTTP',
          serverUsed: 'NONE',
          clientSideExtraction: true,
          timestamp: new Date().toISOString(),
          validationSkipped: true
        }
      };

    } catch (error) {
      console.error('네이티브 자막 추출 실패 (사전 검증된 videoId 사용)', { 
        error: error.message,
        videoId: preValidatedVideoId,
        platform: service.platform
      });
      throw error;
    }
  } else {
    // 🔄 기존 방식: 전체 검증 수행
    console.log('🔄 [네이티브 자막] 기존 방식으로 URL 검증부터 수행');
    return await nativeTranscriptService.extractTranscript(videoUrl, languageCode, includeTimestamp);
  }
} 